import type { TFunction } from 'i18next';

import type { PolicyId, PolicySeoMeta } from '../legal/types';
import { POLICY_SEO } from '../legal';

type SeoFields = Pick<PolicySeoMeta, 'title' | 'description' | 'breadcrumb'>;

function readSeo(t: TFunction, key: string, fallback: string): string {
  const value = t(key, { defaultValue: '' });
  return value && value !== key ? value : fallback;
}

export function getLocalizedPolicySeo(t: TFunction, policyId: PolicyId): PolicySeoMeta {
  const fallback = POLICY_SEO[policyId];
  const base = `content.seo.policies.${policyId}`;
  return {
    ...fallback,
    title: readSeo(t, `${base}.title`, fallback.title),
    description: readSeo(t, `${base}.description`, fallback.description),
    breadcrumb: readSeo(t, `${base}.breadcrumb`, fallback.breadcrumb),
  };
}

export function getLocalizedAboutSeo(t: TFunction): SeoFields {
  return {
    title: readSeo(t, 'content.seo.about.title', 'About GoGrabJob — AI Job Search Platform, Made in India'),
    description: readSeo(
      t,
      'content.seo.about.description',
      'We help job seekers discover matched roles, tailor their resume for each application, and stay organised — without selling your data. Learn our story.',
    ),
    breadcrumb: readSeo(t, 'content.seo.about.breadcrumb', 'About Us'),
  };
}

export function getLocalizedFaqSeo(t: TFunction): SeoFields {
  return {
    title: readSeo(t, 'content.seo.faq.title', 'GoGrabJob FAQs — Job Search, Resume Tailoring & ATS'),
    description: readSeo(
      t,
      'content.seo.faq.description',
      'How GoGrabJob discovers and ranks jobs, tailors your existing resume for each role, scores ATS fit, and keeps your search in one workspace.',
    ),
    breadcrumb: readSeo(t, 'content.seo.faq.breadcrumb', 'FAQs'),
  };
}

export function getLocalizedPricingSeo(t: TFunction): SeoFields {
  return {
    title: readSeo(t, 'content.seo.pricing.title', 'Pricing — AI Resume Optimizer Plans | GoGrabJob'),
    description: readSeo(
      t,
      'content.seo.pricing.description',
      'Start free. Upgrade when your search intensifies — ranked job discovery, resume tailoring, ATS scoring, and cover letters on paid plans.',
    ),
    breadcrumb: readSeo(t, 'content.seo.pricing.breadcrumb', 'Pricing'),
  };
}

export function getLocalizedContactSeo(t: TFunction): SeoFields {
  return {
    title: readSeo(t, 'content.seo.contact.title', 'Contact GoGrabJob — Support & Office in Bengaluru'),
    description: readSeo(
      t,
      'content.seo.contact.description',
      'Contact GoGrabJob support, sales, or press. Visit Zykes Technologies LLP at Electronic City Phase 1, Bengaluru, or email support@gograbjob.com.',
    ),
    breadcrumb: readSeo(t, 'content.seo.contact.breadcrumb', 'Contact Us'),
  };
}
